

import React, { useState,useEffect } from "react";
import { Container, Row, Col, Table, Form, Button, Badge, Card } from "react-bootstrap";
import { FaEye, FaPrint, FaDownload, FaBox, FaExclamationTriangle } from "react-icons/fa";
import { Link } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import "./CustomerList.css";
import * as XLSX from 'xlsx';
import api from "../services/api";

const InHouseStockReport = () => {
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stockStatusFilter, setStockStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Sample stock data - Fixed soldStock field consistency
  const [stockItems, setStockItems] = useState([]);

  const fetchDeliveryNotes = async () => {
    try {
      // setLoading(true);
      // setError("");
      const response = await api.post("/inhouse_stock_list.php", {});
      
      if (response.data.head.code === 200) {
        setStockItems(response.data.body);
      } else {
        // setError(response.data.head.msg || "Failed to fetch delivery notes");
        setStockItems([]);
      }
    } catch (err) {
      console.error("Error fetching delivery notes:", err);
      // setError("Failed to load delivery notes. Please try again.");
      setStockItems([]);
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
      fetchDeliveryNotes();
    }, []);

  // Get unique categories for filter
  const categories = [...new Set(stockItems.map(item => item.category))];

  const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

const parseYYYYMMDD = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-");
  return new Date(year, month - 1, day);
};




  // Calculate stock value
  const calculateStockValue = () => {
    return stockItems.reduce((total, item) => {
      return total + (item.stock * item.purchaseRate);
    }, 0);
  };

  // Get low stock items
  const getLowStockItems = () => {
    return stockItems.filter(item => item.stock <= 5 && item.stock > 0);
  };

  // Get out of stock items
  const getOutOfStockItems = () => {
    return stockItems.filter(item => item.stock == 0);
  };

  // Get overstock items
  const getOverStockItems = () => {
    return stockItems.filter(item => item.stock > 100);
  };

  // Get stock status badge
  const getStockStatusBadge = (item) => {
    if (item.stock === 0) {
      return <Badge bg="danger">Out of Stock</Badge>;
    } else if (item.stock <= 5) {
      return <Badge bg="warning">Low Stock</Badge>;
    } else if (item.stock > 100) {
      return <Badge bg="info">Overstock</Badge>;
    } else {
      return <Badge bg="success">In Stock</Badge>;
    }
  };

  // Get stock level color
  const getStockLevelColor = (item) => {
    if (item.stock === 0) return 'text-danger';
    if (item.stock <= 5) return 'text-warning';
    if (item.stock > 100) return 'text-info';
    return 'text-success';
  };

  // Helper function to get status string for getStatusClass
  const getStockStatusStringForClass = (item) => {
    if (item.stock === "0") return "Out of Stock";
    if (item.stock <= 5) return "Low Stock";
    if (item.stock > 100) return "Overstock";
    return "In Stock";
  };

  const filteredStockItems = stockItems.filter((item) => {
  // 🔍 Search filter
  const matchesSearch =
    !search ||
    item.product_code?.toLowerCase().includes(search.toLowerCase()) ||
    item.name?.toLowerCase().includes(search.toLowerCase()) ||
    item.supplier_name?.toLowerCase().includes(search.toLowerCase());

  // 📦 Stock status filter
  const stockStatus = getStockStatusStringForClass(item).toLowerCase();

  const matchesStatus =
    !stockStatusFilter ||
    stockStatus === stockStatusFilter;

  return matchesSearch && matchesStatus;
});

  // Helper function for export
  const getStockStatusString = (item) => {
    if (item.stocktock == 0) return "Out of Stock";
    if (item.stocktock <= 5) return "Low Stock";
    if (item.stocktock > 100) return "Overstock";
    return "In Stock";
  };

  const clearDateFilter = () => {
    setFromDate("");
    setToDate("");
  };

  const clearAllFilters = () => {
    setSearch("");
    setCategoryFilter("");
    setStockStatusFilter("");
    setFromDate("");
    setToDate("");
  };

  

  

  // Status class function
  const getStatusClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "in stock")
      return "status-badge status-approved";
    if (s === "low stock")
      return "status-badge status-cancelled";
    if (s === "out of stock")
      return "status-badge status-rejected";
    if (s === "overstock")
      return "status-badge status-progress";
    return "status-badge status-default";
  };

  
// Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentStockItems,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredStockItems, 10);

  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">In-House Stock Report</h2>

      
      <Row className="mb-4">
        <Col xs={6} md={3}>
          <Card className="stat-card text-center border-0 bg-primary text-white">
            <Card.Body>
              <h4 className="mb-1">{stockItems.length}</h4>
              <small>Total Products</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card text-center border-0 bg-warning text-dark">
            <Card.Body>
              <h4 className="mb-1">{getLowStockItems().length}</h4>
              <small>Low Stock Items</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card text-center border-0 bg-danger text-white">
            <Card.Body>
              <h4 className="mb-1">{getOutOfStockItems().length}</h4>
              <small>Out of Stock</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card text-center border-0 bg-info text-white">
            <Card.Body>
              <h4 className="mb-1">{getOverStockItems().length}</h4>
              <small>Overstock Items</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      
      <div className="filters-section mb-4 p-3 border rounded bg-light">
        <Row className="g-3 align-items-end">
          <Col xs={12} md={6} lg={6}>
            <Form.Group>
              <Form.Label>Search Products</Form.Label>
              <Form.Control
                placeholder="Search by product code, name, or supplier"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Form.Group>
          </Col>


          {/* <Col xs={12} md={6} lg={6}>
            <Form.Group>
              <Form.Label>Stock Status</Form.Label>
              <Form.Select
                value={stockStatusFilter}
                onChange={(e) => setStockStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Low Stock">Low Stock</option>
                <option value="Out of Stock">Out of Stock</option>
                <option value="Overstock">Overstock</option>
              </Form.Select>
            </Form.Group>
          </Col> */}

          
        </Row>
      </div>

      
      {(getLowStockItems().length > 0 || getOutOfStockItems().length > 0) && (
        <Row className="mb-3">
          <Col xs={12}>
            <div className="p-3 border rounded bg-warning bg-opacity-10">
              <h6 className="mb-2">
                <FaExclamationTriangle className="me-2 text-warning" />
                Stock Alerts
              </h6>
              <Row>
                {getOutOfStockItems().length > 0 && (
                  <Col md={6}>
                    <strong className="text-danger">Out of Stock:</strong>
                    <div className="small">
                      {getOutOfStockItems().map(item => item.name).join(', ')}
                    </div>
                  </Col>
                )}
                {getLowStockItems().length > 0 && (
                  <Col md={6}>
                    <strong className="text-warning">Low Stock:</strong>
                    <div className="small">
                      {getLowStockItems().map(item => item.productName).join(', ')}
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          </Col>
        </Row>
      )}

      
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <Table bordered responsive className="table-striped customer-table">
            <thead className="table-dark">
              <tr>
                <th width="5%">S.No</th>
                <th width="10%">Product Code</th>
                <th width="20%">Product Name</th>
                <th width="8%" className="text-center">Used Stock</th>
                <th width="8%" className="text-center">Current Stock</th>
                
                <th width="8%" className="text-center">Unit</th>
                <th width="20%">Status</th>
              </tr>
            </thead>
            <tbody>
              {currentStockItems.map((item, index) => (
                <tr key={item.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>
                    <strong>{item.product_code}</strong>
                  </td>
                  <td>
                    <div>
                      <div>{item.name}</div>
                      <small className="text-muted">{item.supplier_name}</small>
                    </div>
                  </td>
                  
                  <td className="text-center">
                    <strong>{item.sold_stock}</strong>
                  </td>
                  <td className={`text-center fw-bold ${getStockLevelColor(item)}`}>
                    {item.stock}
                  </td>
                  <td className="text-center">{item.unit_name}</td>
                  <td>
                    <span className={getStatusClass(getStockStatusStringForClass(item))}>
                      {getStockStatusStringForClass(item)}
                    </span>
                  </td>
                </tr>
              ))}
              {currentStockItems.length === 0 && (
                <tr>
                  <td colSpan="12" className="text-center py-4">
                    <div className="text-muted">
                      <FaBox size={48} className="mb-3" />
                      <p>No stock items found</p>
                      <small>Try adjusting your search or filters</small>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

     
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

    </Container>
  );
};

export default InHouseStockReport;
